const CONFIRMED_VOLUME_TERMS = [
  "increasing",
  "surging",
  "above average",
  "strong",
];

const WEAK_VOLUME_TERMS = [
  "weak",
  "weakening",
  "decreasing",
  "below average",
  "heavy selling",
];

export function analyzeNinaSmartFlow(snapshot = {}) {
  const currentPrice = toNumber(snapshot.currentPrice);
  const vwap = toNumber(snapshot.vwap);
  const ema20 = toNumber(snapshot.ema20);
  const support = firstNumber(snapshot.support, snapshot.dayLow, snapshot.stop);
  const resistance = firstNumber(
    snapshot.resistance,
    snapshot.dayHigh,
    snapshot.targetOne
  );
  const entry = toNumber(snapshot.entry);
  const stop = toNumber(snapshot.stop);
  const targetOne = toNumber(snapshot.targetOne);
  const targetTwo = toNumber(snapshot.targetTwo);

  const trend = deriveTrend({
    currentPrice,
    vwap,
    ema20,
    confidence: snapshot.confidence,
    directionalEvidence: snapshot.directionalEvidence,
    bullishSignals: snapshot.bullishSignals,
    bearishSignals: snapshot.bearishSignals,
  });
  const vwapRead = deriveVwapPosition(currentPrice, vwap);
  const volume = deriveVolumeConfirmation(
    snapshot.volumeLabel,
    snapshot.volumeStrengthScore,
    snapshot.volumeEvidenceAvailable === true
  );
  const pressure = derivePressure({
    trend,
    vwapRead,
    volume,
    institutionalFlowScore: snapshot.institutionalFlowScore,
    riskLevel: snapshot.riskLevel,
  });
  const breakout = deriveBreakoutState({
    currentPrice,
    support,
    resistance,
    volume,
    trend,
    riskLevel: snapshot.riskLevel,
  });
  const plan = deriveTradePlan({
    entry,
    stop,
    targetOne,
    targetTwo,
    tradeBlocked: snapshot.tradeBlocked,
  });
  const liquidity = deriveLiquidity(snapshot);
  const institutionalActivity = deriveInstitutionalActivity(snapshot);
  const explanation = buildExplanation({
    symbol: snapshot.symbol,
    trend,
    vwapRead,
    volume,
    pressure,
    breakout,
    plan,
    liquidity,
    institutionalActivity,
    tradeBlocked: snapshot.tradeBlocked,
  });

  return {
    mode:
      snapshot.mode === "demo" ? "Simulated" : "Authenticated + derived",
    symbol: snapshot.symbol || "Symbol",
    trend,
    vwap: vwapRead,
    volume,
    pressure,
    structure: {
      support,
      resistance,
      supportLabel: formatPrice(support),
      resistanceLabel: formatPrice(resistance),
    },
    breakout,
    liquidity,
    institutionalActivity,
    orderFlow: {
      levelTwo: {
        status: snapshot.levelTwoAvailable ? "Available" : "Unavailable",
        explanation: snapshot.levelTwoAvailable
          ? "Level II depth was supplied to this analysis."
          : "No real Level II depth is connected.",
      },
      timeAndSales: {
        status: snapshot.timeAndSalesAvailable ? "Available" : "Unavailable",
        explanation: snapshot.timeAndSalesAvailable
          ? "Time & Sales prints were supplied to this analysis."
          : "No real Time & Sales feed is connected.",
      },
    },
    plan,
    explanation,
    disposition: getDisposition({
      tradeBlocked: snapshot.tradeBlocked,
      breakout,
      trend,
      volume,
    }),
  };
}

function deriveTrend({
  currentPrice,
  vwap,
  ema20,
  confidence,
  directionalEvidence = "",
  bullishSignals,
  bearishSignals,
}) {
  const evidence = String(directionalEvidence).toLowerCase();
  const bullishCount = toNumber(bullishSignals);
  const bearishCount = toNumber(bearishSignals);
  let direction = "Sideways";

  if (
    (isNumber(bearishCount) &&
      isNumber(bullishCount) &&
      bearishCount > bullishCount) ||
    evidence.includes("bearish") ||
    evidence.includes("do not enter")
  ) {
    direction = "Bearish";
  } else if (
    (isNumber(bullishCount) &&
      isNumber(bearishCount) &&
      bullishCount > bearishCount) ||
    evidence.includes("long setup") ||
    evidence.includes("bullish")
  ) {
    direction = "Bullish";
  } else if (isNumber(currentPrice) && isNumber(vwap)) {
    const aboveVwap = currentPrice > vwap;
    const belowVwap = currentPrice < vwap;
    const emaSupportsBullish = !isNumber(ema20) || currentPrice > ema20;
    const emaSupportsBearish = !isNumber(ema20) || currentPrice < ema20;
    if (aboveVwap && emaSupportsBullish) direction = "Bullish";
    if (belowVwap && emaSupportsBearish) direction = "Bearish";
  }

  const suppliedScore = toNumber(confidence);
  const score = isNumber(suppliedScore)
    ? Math.round(clamp(suppliedScore, 0, 100))
    : null;
  const strength = score === null
    ? "Unavailable"
    : score >= 75
    ? "Strong"
    : score >= 55
    ? "Moderate"
    : "Weak";

  return {
    direction,
    strength,
    score,
    label:
      score === null
        ? `${direction} · strength unavailable`
        : `${strength} ${direction.toLowerCase()}`,
    provenance: "Derived from authenticated price evidence and AI live decision evidence.",
  };
}

function deriveVwapPosition(currentPrice, vwap) {
  if (!isNumber(currentPrice) || !isNumber(vwap) || vwap === 0) {
    return {
      position: "Unavailable",
      distancePercent: null,
      label: "VWAP unavailable",
    };
  }

  const distancePercent = ((currentPrice - vwap) / vwap) * 100;
  const position = Math.abs(distancePercent) <= 0.08
    ? "At VWAP"
    : distancePercent > 0
    ? "Above VWAP"
    : "Below VWAP";

  return {
    position,
    distancePercent: Number(distancePercent.toFixed(2)),
    label: `${position} · ${Math.abs(distancePercent).toFixed(2)}%`,
  };
}

function deriveVolumeConfirmation(
  volumeLabel = "",
  volumeStrengthScore,
  evidenceAvailable = false
) {
  if (!evidenceAvailable) {
    return {
      state: "Unavailable",
      score: null,
      label: "Unavailable",
      provenance: "Unavailable",
    };
  }
  const normalized = String(volumeLabel).toLowerCase();
  const score = firstNumber(
    volumeStrengthScore,
    CONFIRMED_VOLUME_TERMS.some((term) => normalized.includes(term))
      ? 82
      : WEAK_VOLUME_TERMS.some((term) => normalized.includes(term))
      ? 30
      : 55
  );
  const state = score >= 70
    ? "Confirmed"
    : score >= 45
    ? "Developing"
    : "Not confirmed";

  return {
    state,
    score: Math.round(clamp(score, 0, 100)),
    label: volumeLabel
      ? `${state} · ${volumeLabel}`
      : `${state} · estimated`,
    provenance: "Estimated",
  };
}

function derivePressure({
  trend,
  vwapRead,
  volume,
  institutionalFlowScore,
  riskLevel = "",
}) {
  let score = 50;
  if (trend.direction === "Bullish") score += 16;
  if (trend.direction === "Bearish") score -= 16;
  if (vwapRead.position === "Above VWAP") score += 10;
  if (vwapRead.position === "Below VWAP") score -= 10;
  if (volume.state === "Confirmed") {
    score += trend.direction === "Bearish" ? -8 : 8;
  }
  if (isNumber(toNumber(institutionalFlowScore))) {
    score += (toNumber(institutionalFlowScore) - 50) * 0.18;
  }
  if (String(riskLevel).toLowerCase().includes("very high")) score -= 10;
  score = Math.round(clamp(score, 0, 100));

  const side = score >= 58
    ? "Buying pressure"
    : score <= 42
    ? "Selling pressure"
    : "Balanced pressure";

  return {
    side,
    score,
    label: `${side} · ${score}/100`,
    provenance: "Estimated from price, VWAP, trend, and volume—not order flow.",
  };
}

function deriveBreakoutState({
  currentPrice,
  support,
  resistance,
  volume,
  trend,
  riskLevel = "",
}) {
  const normalizedRisk = String(riskLevel).toLowerCase();

  if (
    (isNumber(currentPrice) &&
      isNumber(support) &&
      currentPrice < support) ||
    normalizedRisk.includes("very high") ||
    normalizedRisk.includes("high")
  ) {
    return {
      state: "Breakdown risk",
      tone: "danger",
      explanation: "Price or risk conditions are below the planned structure.",
    };
  }

  const breakingResistance =
    isNumber(currentPrice) &&
      isNumber(resistance) &&
      currentPrice > resistance;

  if (breakingResistance && volume.state === "Not confirmed") {
    return {
      state: "Possible fake breakout",
      tone: "warning",
      explanation: "Price is testing resistance without enough volume confirmation.",
    };
  }

  if (breakingResistance) {
    return {
      state: "Breakout supported",
      tone: "positive",
      explanation: "Price strength and volume support the breakout attempt.",
    };
  }

  if (
    isNumber(currentPrice) &&
    isNumber(resistance) &&
    resistance !== 0 &&
    Math.abs(resistance - currentPrice) / resistance <= 0.025
  ) {
    return {
      state: "Testing resistance",
      tone: "warning",
      explanation: "Price is near resistance; follow-through is still required.",
    };
  }

  return {
    state: trend.direction === "Bearish"
      ? "No bullish breakout"
      : "No active breakout",
    tone: "neutral",
    explanation: "Price remains inside the current support and resistance range.",
  };
}

function deriveTradePlan({
  entry,
  stop,
  targetOne,
  targetTwo,
  tradeBlocked,
}) {
  if (tradeBlocked) {
    return {
      blocked: true,
      entryLow: null,
      entryHigh: null,
      stop,
      targetOne,
      targetTwo,
      entryLabel: "Blocked",
      stopLabel: formatPrice(stop),
      targetOneLabel: formatPrice(targetOne),
      targetTwoLabel: formatPrice(targetTwo),
    };
  }

  const riskPerShare =
    isNumber(entry) && isNumber(stop) ? Math.abs(entry - stop) : 0;
  const padding = Math.max(0.01, riskPerShare * 0.15);
  const entryLow = isNumber(entry) ? entry - padding : null;
  const entryHigh = entry;

  return {
    blocked: false,
    entryLow,
    entryHigh,
    stop,
    targetOne,
    targetTwo,
    entryLabel:
      isNumber(entryLow) && isNumber(entryHigh)
        ? `${formatPrice(entryLow)}–${formatPrice(entryHigh)}`
        : "Unavailable",
    stopLabel: formatPrice(stop),
    targetOneLabel: formatPrice(targetOne),
    targetTwoLabel: formatPrice(targetTwo),
  };
}

function deriveLiquidity(snapshot) {
  const available = snapshot.liquidityAvailable === true;
  const hasEstimatedProxy = snapshot.liquidityProxyAvailable === true;
  const value = available || hasEstimatedProxy
    ? snapshot.liquidityLabel || "Unavailable"
    : "Unavailable";

  return {
    value,
    status: available
      ? "Available"
      : hasEstimatedProxy && value !== "Unavailable"
      ? "Estimated proxy"
      : "Unavailable",
    explanation: available
      ? "Liquidity metrics were supplied to this analysis."
      : hasEstimatedProxy && value !== "Unavailable"
      ? "Estimated from a supplied liquidity proxy; no market-depth feed."
      : "No liquidity metric or valid proxy is connected.",
  };
}

function deriveInstitutionalActivity(snapshot) {
  const hasProxy =
    snapshot.institutionalFlowLabel ||
    isNumber(toNumber(snapshot.institutionalFlowScore));

  if (!hasProxy) {
    return {
      value: "Unavailable",
      status: "Unavailable",
      explanation: "No institutional-activity data or proxy is available.",
    };
  }

  return {
    value: snapshot.institutionalFlowLabel || "Flow proxy",
    status: snapshot.institutionalDataAvailable ? "Available" : "Estimated proxy",
    explanation: snapshot.institutionalDataAvailable
      ? "Institutional-activity data was supplied to this analysis."
      : "Estimated from broad-market flow metrics; not verified institutional executions.",
  };
}

function buildExplanation({
  symbol,
  trend,
  vwapRead,
  volume,
  pressure,
  breakout,
  plan,
  liquidity,
  institutionalActivity,
  tradeBlocked,
}) {
  const planText = tradeBlocked
    ? "The trade plan remains blocked until risk conditions improve."
    : `The suggested entry zone is ${plan.entryLabel}, with protection at ${plan.stopLabel} and initial targets at ${plan.targetOneLabel} and ${plan.targetTwoLabel}.`;
const volumeSentence =
  volume.state === "Unavailable"
    ? `Volume confirmation is unavailable. ${pressure.side} is estimated from authenticated price/VWAP evidence and derived trend structure.`
    : `${volume.state} volume supports an estimated ${pressure.side.toLowerCase()}.`;
return `${symbol || "The symbol"} shows a ${trend.label.toLowerCase()} trend and is ${vwapRead.position.toLowerCase()}. ${volumeSentence} ${breakout.state}: ${breakout.explanation} ${planText} Liquidity is ${liquidity.status.toLowerCase()}, and institutional activity is ${institutionalActivity.status.toLowerCase()}.`;
}

function getDisposition({ tradeBlocked, breakout, trend, volume }) {
  if (tradeBlocked || breakout.tone === "danger") return "Protect capital";
  if (breakout.state === "Breakout supported") return "Monitor follow-through";
  if (trend.direction === "Bullish" && volume.state !== "Not confirmed") {
    return "Wait for planned entry";
  }
  return "Wait for confirmation";
}

function firstNumber(...values) {
  for (const value of values) {
    const numeric = toNumber(value);
    if (isNumber(numeric)) return numeric;
  }
  return null;
}

function toNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
}

function isNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

function formatPrice(value) {
  return isNumber(value) ? `$${value.toFixed(2)}` : "Unavailable";
}
